import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { formatTime, formatDate } from '../../lib/utils';
import { BUILDINGS as CONTRACT_BUILDINGS } from '../../lib/data';

const BUILDINGS = [
  { id: 'B', name: 'Temporary Town Depot', address: '35 Glencoe Drive' },
  { id: 'C', name: 'Depot Portable Trailer', address: '28 McNamara Drive' },
  { id: 'D', name: 'Wastewater Treatment Plant', address: '619 St. Thomas Line' },
  { id: 'E', name: 'Paradise Double Ice Complex', address: '1 Sarah Davis Way' },
  { id: 'F', name: 'Rotary Paradise Youth & Community Centre', address: '1 Sarah Davis Way' },
  { id: 'G', name: 'Paradise Park Concession Building', address: 'Sarah Davis Way' },
  { id: 'H', name: 'Dianne Whalen Soccer Complex', address: 'McNamara Drive' },
  { id: 'I', name: 'St. Thomas Community Centre', address: '2 Neary Road' },
  { id: 'J', name: 'Softball Clubhouse', address: 'Milton Road' },
  { id: 'K', name: 'Peter Barry Duff Memorial Park Building', address: '272 Topsail Pond Road' },
  { id: 'L', name: 'St. Thomas Line Pump Station', address: '3 St. Thomas Line' },
  { id: 'M', name: '35 Glenco Drive (Rented Town Hall Space)', address: '35 Glenco Drive' },
  { id: 'N', name: '1655 Topsail Road (Rented Town Hall Space)', address: '1655 Topsail Road' },
];

// Some buildings are cleaned every week under the base contract; the rest are
// only cleaned when the Town puts them on that week's schedule ("Per rental
// schedule" in the contract data). Without this distinction the report counted
// a building the Town never asked for as a missed clean, which is a gap we'd be
// reporting against ourselves.
function isStandingWeekly(id) {
  const cb = CONTRACT_BUILDINGS.find(b => b.id === id);
  if (!cb || cb.status !== 'active') return false;
  return !/per rental schedule/i.test(cb.window || '');
}

function getWeekRange(offset = 0) {
  const now = new Date();
  const day = now.getDay(); // 0 = Sunday
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((day + 6) % 7) + offset * 7);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return { monday, sunday };
}

function isoDate(d) {
  return d.toISOString().split('T')[0];
}

export default function WeeklyReport() {
  const { profile } = useAuth();
  const [weekOffset, setWeekOffset] = useState(0);
  const [submissions, setSubmissions] = useState([]);
  const [sentReports, setSentReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [scheduledIds, setScheduledIds] = useState(new Set());

  const { monday, sunday } = getWeekRange(weekOffset);
  const weekLabel = `${formatDate(monday)} – ${formatDate(sunday)}`;
  const isCurrentWeek = weekOffset === 0;

  async function loadData() {
    setLoading(true);
    setSendResult(null);
    const { monday: m, sunday: s } = getWeekRange(weekOffset);
    const [subRes, sentRes, schedRes] = await Promise.all([
      supabase
        .from('checklist_submissions')
        .select('*')
        .gte('sign_in', m.toISOString())
        .lte('sign_in', s.toISOString())
        .order('sign_in', { ascending: true }),
      supabase
        .from('weekly_report_log')
        .select('*')
        .eq('week_start', isoDate(m))
        .order('sent_at', { ascending: false }),
      // What the Town actually asked to be cleaned this week — needed to tell
      // a genuine miss apart from a building that was never on the schedule.
      supabase
        .from('rental_schedules')
        .select('building_id')
        .eq('week_start', isoDate(m)),
    ]);
    const firstErr = subRes.error || sentRes.error || schedRes.error;
    if (firstErr) {
      console.error('Weekly report load failed:', firstErr);
      setLoadError(firstErr.message);
      setLoading(false);
      return;
    }
    setLoadError(null);
    setSubmissions(subRes.data || []);
    setSentReports(sentRes.data || []);
    setScheduledIds(new Set((schedRes.data || []).map(r => r.building_id)));
    setLoading(false);
  }

  useEffect(() => {
    loadData();
  }, [weekOffset]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group submissions by building
  const byBuilding = BUILDINGS.map(b => {
    const subs = submissions.filter(s => s.building_id === b.id);
    const maintenanceFlags = subs.filter(s => s.maintenance_flag);
    return {
      ...b, subs, maintenanceFlags,
      hasActivity: subs.length > 0,
      scheduled: scheduledIds.has(b.id) || isStandingWeekly(b.id),
    };
  });

  const activeBuildings = byBuilding.filter(b => b.hasActivity);
  // A building with no cleanings is only a miss if it was due this week.
  const missedBuildings = byBuilding.filter(b => !b.hasActivity && b.scheduled);
  const notScheduledBuildings = byBuilding.filter(b => !b.hasActivity && !b.scheduled);
  const expectedCount = byBuilding.filter(b => b.scheduled).length;
  const totalSubmissions = submissions.length;
  const maintenanceCount = submissions.filter(s => s.maintenance_flag).length;
  const gpsIssueCount = submissions.filter(s => s.gps_verified === false && s.gps_error !== 'no_geolocation').length;
  const alreadySent = sentReports.length > 0;

  // Build email body
  function buildEmailBody() {
    const lines = [];
    lines.push(`WEEKLY CHECKLIST REPORT — TC Janitorial Inc.`);
    lines.push(`Contract: TOP-25-27 | Town of Paradise`);
    lines.push(`Week: ${weekLabel}`);
    lines.push(`Submitted by: ${profile?.full_name || 'Supervisor'}`);
    lines.push(`Submitted on: ${new Date().toLocaleDateString('en-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`);
    lines.push(``);
    lines.push(`SUMMARY`);
    lines.push(`Total checklist submissions: ${totalSubmissions}`);
    lines.push(`Buildings cleaned: ${activeBuildings.length} of ${expectedCount} scheduled this week`);
    if (maintenanceCount > 0) lines.push(`Maintenance issues flagged: ${maintenanceCount}`);
    if (missedBuildings.length > 0) lines.push(`Scheduled but no cleaning logged: ${missedBuildings.map(b => b.id + ' — ' + b.name).join(', ')}`);
    lines.push(``);
    lines.push(`BUILDING BREAKDOWN`);
    lines.push(`---`);

    for (const b of activeBuildings) {
      lines.push(``);
      lines.push(`${b.id} — ${b.name}`);
      lines.push(`Civic Address: ${b.address}, Paradise NL`);
      lines.push(`Cleanings this week: ${b.subs.length}`);
      for (const sub of b.subs) {
        lines.push(`  • ${formatDate(sub.sign_in)} | Worker: ${sub.worker_name} | In: ${formatTime(sub.sign_in)} | Out: ${formatTime(sub.sign_out)} | Tasks: ${sub.tasks_done}/${sub.tasks_total}`);
        if (sub.maintenance_flag) lines.push(`    ⚠ MAINTENANCE: ${sub.maintenance_note || 'Flagged — see notes'}`);
        if (sub.notes) lines.push(`    Notes: ${sub.notes}`);
        if (sub.gps_verified === false && sub.gps_error !== 'no_geolocation') lines.push(`    ⚠ GPS: Location could not be verified`);
      }
    }

    if (missedBuildings.length > 0) {
      lines.push(``);
      lines.push(`SCHEDULED BUT NO CLEANING LOGGED`);
      for (const b of missedBuildings) {
        lines.push(`  • ${b.id} — ${b.name} (${b.address})`);
      }
    }

    if (notScheduledBuildings.length > 0) {
      lines.push(``);
      lines.push(`NOT SCHEDULED THIS WEEK (no cleaning required)`);
      for (const b of notScheduledBuildings) {
        lines.push(`  • ${b.id} — ${b.name}`);
      }
    }

    lines.push(``);
    lines.push(`---`);
    lines.push(`This report was generated and submitted via TraverseClean.`);
    lines.push(`TC Janitorial Inc. | info@fabiantraverse.com`);
    return lines.join('\n');
  }

  async function handleSend() {
    setSending(true);
    setSendResult(null);
    try {
      const emailBody = buildEmailBody();

      // Call Supabase Edge Function to send email
      const { error } = await supabase.functions.invoke('send-weekly-report', {
        body: {
          to: 'jlawrence@paradise.ca',
          subject: `TC Janitorial — Weekly Checklist Report — ${weekLabel}`,
          body: emailBody,
          weekStart: isoDate(monday),
          weekEnd: isoDate(sunday),
          submittedBy: profile?.full_name || 'Supervisor',
          totalSubmissions,
        },
      });

      if (error) throw error;

      // Log the send. This used to run without checking { error } at all —
      // if this insert failed after the email had already gone out, the
      // failure was invisible: the UI would still say "sent successfully",
      // but `sentReports` would stay empty on reload, inviting someone to
      // hit Send again and email Jason a duplicate report.
      const { error: logError } = await supabase.from('weekly_report_log').insert([{
        week_start: isoDate(monday),
        week_end: isoDate(sunday),
        sent_by: profile?.id,
        sent_by_name: profile?.full_name || 'Supervisor',
        sent_to: 'jlawrence@paradise.ca',
        total_submissions: totalSubmissions,
        buildings_covered: activeBuildings.length,
        email_body: emailBody,
      }]);

      if (logError) {
        console.error('Weekly report log error:', logError);
        setSendResult({ success: true, logFailed: true, error: logError.message });
      } else {
        setSendResult({ success: true });
      }
      await loadData();
    } catch (err) {
      console.error('Send error:', err);
      setSendResult({ success: false, error: err.message });
    }
    setSending(false);
    setPreviewMode(false);
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <div style={s.title}>Weekly Report</div>
          <div style={s.subtitle}>Compile and send to Jason Lawrence at the Town</div>
        </div>
      </div>

      {/* Week selector */}
      <div style={s.weekNav}>
        <button style={s.weekBtn} onClick={() => setWeekOffset(w => w - 1)}>‹ Previous</button>
        <div style={s.weekLabel}>
          <div style={s.weekRange}>{weekLabel}</div>
          {isCurrentWeek && <div style={s.currentWeekChip}>Current Week</div>}
        </div>
        <button style={{ ...s.weekBtn, opacity: isCurrentWeek ? 0.3 : 1 }}
          disabled={isCurrentWeek}
          onClick={() => setWeekOffset(w => w + 1)}>Next ›</button>
      </div>

      {/* Send status */}
      {alreadySent && (
        <div style={s.sentBanner}>
          <div style={{ color: '#2F7A4F', fontWeight: 700, fontSize: 13 }}>✓ Report sent to Jason Lawrence</div>
          <div style={{ color: '#5B6B72', fontSize: 12, marginTop: 2 }}>
            Sent {new Date(sentReports[0].sent_at).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })} by {sentReports[0].sent_by_name}
          </div>
        </div>
      )}

      {sendResult && (
        <div style={{ ...s.sentBanner, background: sendResult.success ? (sendResult.logFailed ? 'rgba(181,115,10,0.08)' : 'rgba(47,122,79,0.10)') : '#FBF1EF', borderColor: sendResult.success ? (sendResult.logFailed ? '#B5730A' : '#2F7A4F') : '#B33A2E' }}>
          {sendResult.success && sendResult.logFailed ? (
            <>
              <div style={{ color: '#96600A', fontWeight: 700 }}>⚠ Email sent, but the send record failed to save ({sendResult.error})</div>
              <div style={{ color: '#5B6B72', fontSize: 12, marginTop: 2 }}>Jason received the report — but check with him before sending again, since this app won't remember it was sent.</div>
            </>
          ) : sendResult.success
            ? <div style={{ color: '#2F7A4F', fontWeight: 700 }}>✓ Report sent successfully to jlawrence@paradise.ca</div>
            : <div style={{ color: '#B33A2E', fontWeight: 700 }}>✗ Send failed: {sendResult.error}</div>}
        </div>
      )}

      {loading ? (
        <div style={s.loading}>Loading submissions...</div>
      ) : loadError ? (
        <div style={{ ...s.sentBanner, background: '#FBF1EF', borderColor: '#B33A2E' }}>
          <div style={{ color: '#B33A2E', fontWeight: 700 }}>✗ Couldn't load this week's data: {loadError}</div>
          <button style={{ ...s.weekBtn, marginTop: 8 }} onClick={loadData}>Retry</button>
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div style={s.summaryRow}>
            <div style={s.summaryCard}>
              <div style={s.summaryNum}>{totalSubmissions}</div>
              <div style={s.summaryLabel}>Cleanings Logged</div>
            </div>
            <div style={s.summaryCard}>
              <div style={{ ...s.summaryNum, color: activeBuildings.length >= expectedCount ? '#2F7A4F' : '#B5730A' }}>{activeBuildings.length}/{expectedCount}</div>
              <div style={s.summaryLabel}>Scheduled Buildings Cleaned</div>
            </div>
            <div style={s.summaryCard}>
              <div style={{ ...s.summaryNum, color: maintenanceCount > 0 ? '#B5730A' : '#2F7A4F' }}>{maintenanceCount}</div>
              <div style={s.summaryLabel}>Maintenance Issues</div>
            </div>
            <div style={s.summaryCard}>
              <div style={{ ...s.summaryNum, color: gpsIssueCount > 0 ? '#B5730A' : '#2F7A4F' }}>{gpsIssueCount}</div>
              <div style={s.summaryLabel}>Location Not Verified</div>
            </div>
          </div>

          {/* Real gaps only — buildings that were due this week and have no
              cleaning logged. Buildings the Town didn't schedule are listed
              separately below so they can't be mistaken for misses. */}
          {missedBuildings.length > 0 && (
            <div style={s.warningBox}>
              <div style={{ color: '#96600A', fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
                ⚠️ {missedBuildings.length} scheduled building{missedBuildings.length !== 1 ? 's' : ''} with no cleaning logged
              </div>
              {missedBuildings.map(b => (
                <div key={b.id} style={{ color: '#1A2B33', fontSize: 12 }}>{b.id} — {b.name}</div>
              ))}
            </div>
          )}

          {notScheduledBuildings.length > 0 && (
            <div style={{ margin: '0 12px 12px', background: '#F5F3EF', border: '1px solid #E4E0D8', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ color: '#5B6B72', fontWeight: 700, fontSize: 12, marginBottom: 4 }}>
                Not scheduled this week — no cleaning required ({notScheduledBuildings.length})
              </div>
              <div style={{ color: '#1A2B33', fontSize: 12 }}>
                {notScheduledBuildings.map(b => `${b.id} — ${b.name}`).join(' · ')}
              </div>
            </div>
          )}

          {/* Building breakdown */}
          {activeBuildings.length === 0 ? (
            <div style={s.empty}>No checklist submissions found for this week.</div>
          ) : (
            <div style={s.buildingList}>
              <div style={s.sectionLabel}>SUBMISSIONS THIS WEEK</div>
              {activeBuildings.map(b => (
                <div key={b.id} style={s.buildingCard}>
                  <div style={s.buildingHeader}>
                    <div>
                      <div style={s.buildingName}>{b.id} — {b.name}</div>
                      <div style={s.buildingAddr}>{b.address}, Paradise NL</div>
                    </div>
                    <div style={s.buildingCount}>{b.subs.length} clean{b.subs.length !== 1 ? 's' : ''}</div>
                  </div>
                  {b.subs.map((sub, i) => (
                    <div key={i} style={s.subRow}>
                      <div style={s.subDate}>{formatDate(sub.sign_in)}</div>
                      <div style={s.subWorker}>{sub.worker_name}</div>
                      <div style={s.subTimes}>{formatTime(sub.sign_in)} – {formatTime(sub.sign_out)}</div>
                      <div style={s.subTasks}>{sub.tasks_done}/{sub.tasks_total} tasks</div>
                      {sub.gps_verified && <span style={s.gpsOk}>📍</span>}
                      {sub.gps_verified === false && sub.gps_error !== 'no_geolocation' && <span style={s.gpsWarn}>📍?</span>}
                    </div>
                  ))}
                  {b.maintenanceFlags.length > 0 && (
                    <div style={s.maintFlag}>
                      🔧 {b.maintenanceFlags.length} maintenance issue{b.maintenanceFlags.length !== 1 ? 's' : ''} flagged
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Preview / Send */}
          {!previewMode ? (
            <div style={s.sendSection}>
              <div style={s.sendInfo}>
                Sends weekly checklist report to Jason Lawrence at jlawrence@paradise.ca. Your name will be recorded as the supervisor who approved and submitted this report.
              </div>
              <button style={s.previewBtn} onClick={() => setPreviewMode(true)}>
                Preview Report Before Sending
              </button>
            </div>
          ) : (
            <div style={s.previewSection}>
              <div style={s.previewHeader}>
                <div style={s.previewTitle}>Report Preview</div>
                <button style={s.closePreview} onClick={() => setPreviewMode(false)}>✕</button>
              </div>
              <pre style={s.previewBody}>{buildEmailBody()}</pre>
              {alreadySent && (
                <div style={{ margin: '0 14px 12px', padding: '8px 12px', borderRadius: 8, background: 'rgba(181,115,10,0.08)', border: '1px solid #B5730A', color: '#96600A', fontSize: 12 }}>
                  ⚠ This week's report was already sent {new Date(sentReports[0].sent_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })} by {sentReports[0].sent_by_name}. Sending again will email Jason a duplicate.
                </div>
              )}
              <div style={s.previewActions}>
                <button style={s.cancelSendBtn} onClick={() => setPreviewMode(false)}>Back</button>
                <button
                  style={sending ? { ...s.sendBtn, opacity: 0.5 } : s.sendBtn}
                  disabled={sending}
                  onClick={() => {
                    if (alreadySent && !window.confirm('This week\'s report was already sent. Send a duplicate anyway?')) return;
                    handleSend();
                  }}>
                  {sending ? 'Sending...' : alreadySent ? 'Send Duplicate Report →' : `Send to jlawrence@paradise.ca`}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const s = {
  page: { padding: '0 0 60px', background: '#F5F3EF', minHeight: 'calc(100vh - 130px)' },
  header: { padding: '20px 20px 12px' },
  title: { fontSize: 22, fontWeight: 800, color: '#1A2B33', letterSpacing: '-0.5px' },
  subtitle: { fontSize: 13, color: '#5B6B72', marginTop: 2 },
  weekNav: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px 12px', gap: 8 },
  weekBtn: { background: '#FFFFFF', border: '1px solid #E4E0D8', color: '#5B6B72', borderRadius: 8, padding: '8px 14px', fontSize: 14, cursor: 'pointer' },
  weekLabel: { textAlign: 'center', flex: 1 },
  weekRange: { color: '#1A2B33', fontWeight: 700, fontSize: 14 },
  currentWeekChip: { display: 'inline-block', background: 'rgba(31,58,71,0.08)', color: '#1F3A47', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, marginTop: 4 },
  sentBanner: { margin: '0 12px 12px', background: 'rgba(47,122,79,0.10)', border: '1px solid #2F7A4F', borderRadius: 10, padding: '12px 16px' },
  loading: { padding: 40, textAlign: 'center', color: '#5B6B72' },
  summaryRow: { display: 'flex', gap: 8, padding: '0 12px 12px' },
  summaryCard: { flex: 1, background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 10, padding: '12px 8px', textAlign: 'center' },
  summaryNum: { fontSize: 24, fontWeight: 800, color: '#1A2B33' },
  summaryLabel: { fontSize: 11, color: '#5B6B72', marginTop: 2 },
  warningBox: { margin: '0 12px 12px', background: 'rgba(181,115,10,0.08)', border: '1px solid #B5730A', borderRadius: 10, padding: '12px 14px' },
  empty: { margin: '0 12px', padding: 24, textAlign: 'center', color: '#5B6B72', fontSize: 14, border: '1px dashed #E4E0D8', borderRadius: 10 },
  sectionLabel: { fontSize: 10, fontWeight: 700, letterSpacing: '1.5px', color: '#5B6B72', padding: '4px 0 8px', textTransform: 'uppercase' },
  buildingList: { padding: '0 12px', display: 'flex', flexDirection: 'column', gap: 8 },
  buildingCard: { background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 12, padding: 14 },
  buildingHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 },
  buildingName: { fontSize: 14, fontWeight: 700, color: '#1A2B33' },
  buildingAddr: { fontSize: 11, color: '#5B6B72', marginTop: 2 },
  buildingCount: { fontSize: 12, color: '#2D5F7C', fontWeight: 700 },
  subRow: { display: 'flex', gap: 8, alignItems: 'center', padding: '5px 0', borderTop: '1px solid #E4E0D8', flexWrap: 'wrap' },
  subDate: { fontSize: 12, color: '#5B6B72', minWidth: 90 },
  subWorker: { fontSize: 12, color: '#1A2B33', fontWeight: 600, flex: 1 },
  subTimes: { fontSize: 11, color: '#5B6B72' },
  subTasks: { fontSize: 11, color: '#5B6B72' },
  gpsOk: { fontSize: 12, title: 'GPS verified' },
  gpsWarn: { fontSize: 12, color: '#B5730A' },
  maintFlag: { marginTop: 8, fontSize: 12, color: '#B5730A', fontWeight: 600 },
  sendSection: { margin: '16px 12px 0', display: 'flex', flexDirection: 'column', gap: 10 },
  sendInfo: { fontSize: 13, color: '#5B6B72', lineHeight: 1.5, background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 10, padding: '12px 14px' },
  previewBtn: { background: '#FFFFFF', border: '1px solid #E4E0D8', color: '#1F3A47', borderRadius: 10, padding: '13px 0', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  previewSection: { margin: '12px 12px 0', background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 12, overflow: 'hidden' },
  previewHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #E4E0D8' },
  previewTitle: { fontSize: 14, fontWeight: 700, color: '#1A2B33' },
  closePreview: { background: 'none', border: 'none', color: '#5B6B72', fontSize: 18, cursor: 'pointer' },
  previewBody: { padding: '14px 16px', fontSize: 11, color: '#5B6B72', lineHeight: 1.6, overflowX: 'auto', whiteSpace: 'pre-wrap', maxHeight: 400, overflowY: 'auto', margin: 0, fontFamily: 'monospace' },
  previewActions: { display: 'flex', gap: 10, padding: '12px 14px', borderTop: '1px solid #E4E0D8' },
  cancelSendBtn: { flex: 1, background: '#FFFFFF', border: '1px solid #E4E0D8', color: '#5B6B72', borderRadius: 8, padding: '12px 0', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  sendBtn: { flex: 2, background: '#f97316', border: 'none', color: '#FFFFFF', borderRadius: 8, padding: '12px 0', fontWeight: 800, fontSize: 14, cursor: 'pointer' },
};